import { useEffect, useState } from 'react'
import { supabase } from '@/config/supabase'
import { MessageSquare, Copy, Check, Send } from 'lucide-react'
import { fillTemplate } from '@/lib/lead-utils'
import type { LeadWithValuation, MessageTemplate } from '@/types'

interface Props {
  lead: LeadWithValuation
  onClose: () => void
}

export function TemplateMessages({ lead, onClose }: Props) {
  const [templates, setTemplates] = useState<MessageTemplate[]>([])
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    const { data } = await supabase
      .from('message_templates_cagliari')
      .select('*')
      .order('sort_order')

    if (data) setTemplates(data as MessageTemplate[])
  }

  const handleCopy = async (template: MessageTemplate) => {
    const filled = fillTemplate(template.body, lead)
    await navigator.clipboard.writeText(filled)
    setCopied(template.id)
    setTimeout(() => setCopied(null), 2000)
  }

  const handleSendWhatsApp = (template: MessageTemplate) => {
    if (!lead.phone) return
    const filled = fillTemplate(template.body, lead)

    // Costruisci URL WhatsApp con il messaggio del template
    let cleaned = lead.phone.replace(/[\s\-()]/g, '')
    if (cleaned.startsWith('0')) cleaned = '39' + cleaned
    else if (!cleaned.startsWith('+') && !cleaned.startsWith('39')) cleaned = '39' + cleaned
    cleaned = cleaned.replace(/^\+/, '')

    window.open(`https://wa.me/${cleaned}?text=${encodeURIComponent(filled)}`, '_blank')
  }

  const handleSendEmail = (template: MessageTemplate) => {
    const filled = fillTemplate(template.body, lead)
    const subject = encodeURIComponent(template.title)
    const body = encodeURIComponent(filled)
    window.open(`mailto:${lead.email}?subject=${subject}&body=${body}`)
  }

  const leadName = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Cliente'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Messaggi rapidi</h3>
          </div>
          <p className="text-sm text-gray-500 mt-1">Per {leadName}</p>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
          {templates.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              Nessun template configurato. Esegui la migration SQL per aggiungere i template di default.
            </p>
          ) : (
            templates.map((t) => {
              const filled = fillTemplate(t.body, lead)
              return (
                <div key={t.id} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-900">{t.title}</span>
                    <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                      t.channel === 'whatsapp' ? 'bg-green-100 text-green-700' :
                      t.channel === 'email' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {t.channel === 'whatsapp' ? 'WhatsApp' : t.channel === 'email' ? 'Email' : 'SMS'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2 leading-relaxed">{filled}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCopy(t)}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      {copied === t.id ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                      {copied === t.id ? 'Copiato!' : 'Copia'}
                    </button>
                    {t.channel === 'whatsapp' && lead.phone && (
                      <button
                        onClick={() => handleSendWhatsApp(t)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                      >
                        <Send className="h-3 w-3" />
                        WhatsApp
                      </button>
                    )}
                    {t.channel === 'email' && (
                      <button
                        onClick={() => handleSendEmail(t)}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                      >
                        <Send className="h-3 w-3" />
                        Email
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  )
}
